/* text-reveal — mask reveal for headings (GSAP + SplitText),
   the same technique itsneat.digital uses on its hero title.

   Usage: add `data-reveal` to any heading.
     data-reveal           → split into lines via SplitText (default)
     data-reveal="words"   → split into words (tighter stagger + soft fade)
     data-reveal="rows"    → treat the heading's existing child elements as the
                             lines (use when the markup already builds its own
                             lines from block-level spans, e.g. the hero —
                             SplitText can't reliably re-split those)
   Each piece is wrapped in an overflow-clip mask, parked below it, then slid up
   with a stagger when the heading scrolls into view (once).

   Progressive enhancement: if GSAP/SplitText are missing, the visitor prefers
   reduced motion, IntersectionObserver is unavailable, or we're in the Shopify
   theme editor, the headings are simply left visible and un-animated. */
(function () {
  const targets = document.querySelectorAll('[data-reveal]');
  if (!targets.length) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const inEditor = window.Shopify && window.Shopify.designMode;
  const ready = window.gsap && window.SplitText && 'IntersectionObserver' in window;

  // Bail out → leave the text visible exactly as authored.
  if (reduceMotion || inEditor || !ready) return;

  const gsap = window.gsap;
  gsap.registerPlugin(window.SplitText);

  // Hide now (before fonts settle) so there's no flash of un-masked text.
  gsap.set(targets, { autoAlpha: 0 });

  // Wrap an element in a block-level overflow-clip mask so it can slide up from
  // behind a hard edge. Returns the mask.
  function maskRow(row) {
    const mask = document.createElement('span');
    mask.className = 'reveal-line-mask';
    mask.style.display = 'block';
    mask.style.overflow = 'hidden';
    // Breathing room so tall display glyphs (ascenders/descenders) and italic
    // overhangs aren't clipped at rest; negative margin keeps layout put.
    mask.style.padding = '0.18em 0.12em';
    mask.style.margin = '-0.18em -0.12em';
    row.parentNode.insertBefore(mask, row);
    mask.appendChild(row);
    return mask;
  }

  function reveal(el) {
    const mode = el.dataset.reveal; // '', 'words', or 'rows'
    const byWord = mode === 'words';
    const byRow = mode === 'rows';

    // Split + park LAZILY — only once the heading reaches its trigger, never all
    // at load. SplitText forces a synchronous layout; doing all ~25 headings at
    // once on a cold load is a main-thread spike that stutters the first reveals.
    // Splitting one heading at a time as you scroll spreads that cost out.
    // `willChange: transform` promotes each piece to its own compositor layer
    // BEFORE the tween, so the first frame doesn't have to create the layer
    // mid-animation (the visible "pop"); cleared on complete to free the layer.
    function build() {
      let parts;
      if (byRow) {
        // The markup already defines the lines (block-level child spans). Mask and
        // animate those directly — don't let SplitText re-measure them as one.
        parts = Array.from(el.children);
        parts.forEach(maskRow);
        gsap.set(parts, { yPercent: 110, willChange: 'transform' });
      } else {
        const split = window.SplitText.create(el, {
          type: byWord ? 'words' : 'lines',
          mask: byWord ? 'words' : 'lines', // wrap each piece in an overflow-clip mask
          wordsClass: 'reveal-word',
          linesClass: 'reveal-line',
        });
        parts = byWord ? split.words : split.lines;
        // Park each piece below its mask. Words also fade so they don't pop in.
        gsap.set(parts, byWord
          ? { yPercent: 110, opacity: 0, willChange: 'transform, opacity' }
          : { yPercent: 110, willChange: 'transform' });
      }
      gsap.set(el, { autoAlpha: 1 }); // wrapper visible; pieces still hidden
      return parts;
    }

    const io = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      io.disconnect();
      const parts = build();
      gsap.to(parts, {
        yPercent: 0,
        opacity: 1,
        // Words are smaller and many, so they ride in faster and closer together.
        duration: byWord ? 0.7 : 0.9,
        stagger: byWord ? 0.08 : 0.25,
        ease: 'power3.out',
        onComplete: () => gsap.set(parts, { willChange: 'auto' }),
      });
      // rootMargin bottom -15% pulls the trigger line up by 15% of the viewport,
      // so the heading must scroll a bit past the bottom edge before it starts.
    }, { threshold: 0.2, rootMargin: '0px 0px -15% 0px' });

    io.observe(el);
  }

  // Split after fonts load so line breaks are measured correctly.
  const run = () => targets.forEach(reveal);
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(run);
  } else {
    run();
  }
})();
